import type { EnvironmentType, EnvironmentV2Resource } from "@octopusdeploy/octopus-server-client";
import { sortBy } from "lodash";
import type * as React from "react";
import { ChipIcon, EnvironmentChip, ContextualMissingChip, ParentEnvironmentChip, EphemeralEnvironmentChip } from "~/components/Chips";
import { DeprecatedMultiSelect } from "~/components/MultiSelect/MultiSelect";
import type { FocusableComponent } from "~/components/VirtualListWithKeyboard/FocusableComponent";
import type { SelectItem } from "~/components/VirtualListWithKeyboard/SelectItem";
import type { FormFieldProps } from "~/components/form";
import Lookup from "../Lookup";
import type { Item } from "../VirtualListWithKeyboard/VirtualListWithKeyboard";
type EnvironmentV2MultiSelectDataItem = {
    Id: string;
    Name: string;
    Type: EnvironmentType;
};
export const EnvironmentV2TypedMultiSelect = DeprecatedMultiSelect<EnvironmentV2MultiSelectDataItem>();
interface EnvironmentV2MultiSelectBase extends FormFieldProps<string[]> {
    openOnFocus?: boolean;
    autoFocus?: boolean;
    label?: string | JSX.Element;
    error?: string;
    hideFloatingLabel?: boolean;
    multiSelectRef?(component: FocusableComponent | null): void;
    accessibleName?: string;
    onRemove?: (id: string) => void;
    fallbackLabel?: string;
    fallbackDescription?: string;
    lookupCollection?: EnvironmentV2MultiSelectDataItem[];
}
interface EnvironmentMultiSelectWithEnviromentV2Resources extends EnvironmentV2MultiSelectBase {
    environments: EnvironmentV2Resource[];
    items?: never;
}
interface EnvironmentMultiSelectWithoutEnvironmentV2Resources extends EnvironmentV2MultiSelectBase {
    environments?: never;
    items: EnvironmentV2MultiSelectDataItem[];
}
type EnvironmentV2MultiSelectProps = EnvironmentMultiSelectWithEnviromentV2Resources | EnvironmentMultiSelectWithoutEnvironmentV2Resources;
export const EnvironmentV2MultiSelect: React.FC<EnvironmentV2MultiSelectProps> = (props) => {
    let computedItems: EnvironmentV2MultiSelectDataItem[] = [];
    if (props.environments) {
        computedItems = sortBy(props.environments, (x) => x.SortOrder).map((e) => ({ Id: e.Id, Name: e.Name, Type: e.Type }));
    }
    else if (props.items) {
        computedItems = props.items;
    }
    const chipRenderer = (r: EnvironmentV2Resource | SelectItem, onRequestDelete: () => void) => {
        return (<Lookup lookupCollection={props.lookupCollection ?? computedItems} lookupId={r.Id} getIdFromElement={(element: EnvironmentV2MultiSelectDataItem) => element.Id} render={(item: EnvironmentV2MultiSelectDataItem) => {
                switch (item.Type) {
                    case "Ephemeral":
                        return <EphemeralEnvironmentChip deleteButtonAccessibleName={`Delete ${item.Name}`} onRequestDelete={onRequestDelete} environmentName={item.Name}/>;
                    case "Parent":
                        return <ParentEnvironmentChip deleteButtonAccessibleName={`Delete ${item.Name}`} onRequestDelete={onRequestDelete} environmentName={item.Name}/>;
                    case "Static":
                    default:
                        return <EnvironmentChip deleteButtonAccessibleName={`Delete ${item.Name}`} onRequestDelete={onRequestDelete} environmentName={item.Name}/>;
                }
            }} renderFallback={<ContextualMissingChip lookupKey={r.Id} type={ChipIcon.Environment} text={props.fallbackLabel} description={props.fallbackDescription} onRequestDelete={onRequestDelete}/>}/>);
    };
    const itemRenderer = (item: EnvironmentV2MultiSelectDataItem): Item => {
        return {
            primaryText: item.Name,
            secondaryText: item.Type === "Parent" ? "Parent" : undefined,
        };
    };
    return <EnvironmentV2TypedMultiSelect fieldName="environments" renderItem={itemRenderer} renderChip={chipRenderer} accessibleName={props.accessibleName} sortItems={false} items={computedItems} {...props}/>;
};
EnvironmentV2MultiSelect.displayName = "EnvironmentV2MultiSelect"
